import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useDialogFocus } from './useDialogFocus';

interface Props {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode | undefined;
  /** `wide` fits an embedded iframe preview; `default` fits a form; `full` is an editor
   *  laid out by its children (fixed height, no body padding; scrolls only when squeezed). */
  size?: 'default' | 'wide' | 'full' | undefined;
}

const PANEL_SIZE = {
  default: 'max-h-[85vh] max-w-lg',
  wide: 'max-h-[85vh] max-w-5xl',
  full: 'h-[88vh] max-w-6xl',
} as const;

const BODY = {
  default: 'flex-1 overflow-y-auto p-4',
  wide: 'flex-1 overflow-y-auto p-4',
  full: 'flex min-h-0 flex-1 overflow-auto',
} as const;

/**
 * Shared modal frame: Escape and the Close button close it, a click outside does not
 * (a half-made edit is never lost to a stray click). Same layering as
 * CallbackLogDetailModal (z-40 backdrop, z-50 panel) so the two never fight.
 */
export function Modal({ open, title, onClose, children, footer, size = 'default' }: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  const { toFirst, toLast } = useDialogFocus(open, panelRef);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Focus leaving the panel (e.g. Tab out of an embedded iframe) lands here and wraps back */}
        <span tabIndex={0} data-focus-guard="true" onFocus={toLast} className="sr-only" />
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
          className={`flex w-full ${PANEL_SIZE[size]} flex-col overflow-hidden rounded-lg border border-[var(--co-border)] bg-[var(--co-surface)] shadow-lg outline-none`}
        >
          <div className="flex items-center justify-between gap-4 border-b border-[var(--co-border)] px-4 py-3">
            <h2 className="text-[15px] font-semibold text-[var(--co-text)]">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded p-1 text-[var(--co-muted)] transition-colors hover:bg-[var(--co-bg)] hover:text-[var(--co-text)]"
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>
          <div className={BODY[size]}>{children}</div>
          {footer ? (
            <div className="border-t border-[var(--co-border)] px-4 py-3">{footer}</div>
          ) : null}
        </div>
        <span tabIndex={0} data-focus-guard="true" onFocus={toFirst} className="sr-only" />
      </div>
    </>
  );
}
